/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components */
// Ember imports
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { assert } from '@ember/debug';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External imports
import CommentModerationStatus from '@linc-technologies/linced-common/enums/comment-moderation-status';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import PostType from '@linc-technologies/linced-common/enums/post-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import Filter from 'bad-words';
import { classNames } from '@ember-decorators/component';

@classNames('post-comment')
export default class PostComment extends Component {
  @action
  setCommentContent(value) {
    this.commentContent = value;
  }

  // Component versioning
  @tracked visualStyle = 'v1';

  safeWords = ['hui', 'god'];

  // Services
  @service
  session;

  @service
  heroApi;

  @service
  permissions;

  @service
  toaster;

  // Props
  // comment provides a comment resource.
  comment = null;

  // reply provides the callback to handle reply to the comment.
  // expected signature: function(comment: string)
  reply = null;

  // edit provides the callback to handle editing the comment.
  // @param comment {Comment} - the comment record.
  // @param newContent {string} - the updated content for the comment.
  edit() {}

  // delete provides the callback to handle removing the comment.
  // @param comment {Comment} - the comment record.
  delete() {}

  // State
  // commentContextMenuOpen defines the state of the contextual drop down menu.
  commentContextMenuOpen = false;

  // deleteDialogOpen defines the open state in order to delete a comment.
  @tracked
  deleteDialogOpen = false;

  // isEditMode defines the state of being in display or comment edit mode.
  @tracked
  isEditMode = false;

  // commentContent provides a staging hold for the comment in case it gets
  // edited.
  @tracked
  commentContent = '';

  // Methods
  // canEdit calculates if the current user can edit the comment.
  @computed('comment.id', 'session.{personId,scopes.[]}')
  get canEdit() {
    // TODO: actually work out the permission logic required here...
    return this.get('comment.author.id') === this.get('session.personId');
  }

  // canDelete calculates if the current user can delete the comment.
  @computed('comment.id', 'session.{personId,scopes.[]}')
  get canDelete() {
    // Ruleset:
    // - Allow LINC-ED Admins and Managers to delete any comment.
    // - Allow Tenant Admins to delete any comment.
    // - Allow Tenant Staff to delete any comments on their own post.
    // - Allow any to delete their own comments.
    // - Deny all.
    if (this.permissions.meet(Scopes.TENANT_ADMIN)) {
      return true;
    }

    let postAuthorId = this.get('comment.post.author.id');
    let commentAuthorId = this.get('comment.author.id');
    let personId = this.get('session.personId');
    if (!isEmpty(postAuthorId)) {
      let isStaff = this.permissions.equal(Scopes.TENANT_STAFF);
      let isPostAuthor = postAuthorId === personId;
      let isCommentAuthor = commentAuthorId === personId;

      return (isStaff && isPostAuthor) || isCommentAuthor;
    }

    return false;
  }

  // isRemoved checks to see if any content exists in the comment, therefore
  // marking it "removed" if it is an empty string.
  // TODO: implement proper backend support for comment deletion..?
  @computed('comment.content')
  get isRemoved() {
    return isEmpty(this.get('comment.content'));
  }

  // canHaveContextMenu calculates whether the comment context menu should be
  // displayed for the current user.
  @computed('canEdit', 'canDelete', 'isRemoved')
  get canHaveContextMenu() {
    if (this.isRemoved) {
      return false;
    }

    return this.canEdit || this.canDelete;
  }

  // isPending checks if comment is pending for moderation
  @computed('comment.moderationStatus')
  get isPending() {
    return (
      this.get('comment.moderationStatus') ===
      CommentModerationStatus.UNSPECIFIED
    );
  }

  // isApproved checks if comment has been approved
  @computed('comment.moderationStatus')
  get isApproved() {
    return (
      this.get('comment.moderationStatus') === CommentModerationStatus.ACCEPTED
    );
  }

  // isDeclined checks if comment has been declined
  @computed('comment.moderationStatus')
  get isDeclined() {
    return (
      this.get('comment.moderationStatus') === CommentModerationStatus.DECLINED
    );
  }

  // requiresModeration calculates whether a comment needs to be moderated
  // if it is in the community notices and the comment author is not trusted
  @computed('isRemoved', 'comment.{post.postType,author.type}')
  get requiresModeration() {
    let isCommunityNoticePost =
      this.get('comment.post.postType') === PostType.COMMUNITY_NOTICE;
    if (!isCommunityNoticePost) {
      // Only target community notices
      return false;
    }
    if (this.isRemoved) {
      // Removed comments do not need moderation
      return false;
    }
    // Caregivers and learners need their comments moderated
    let authorType = this.get('comment.author.type');
    return (
      authorType === PersonType.CAREGIVER || authorType === PersonType.LEARNER
    );
  }

  // canModerate calculates whether a user can moderate a comment,
  // based on the permissions rule set
  // enables moderation buttons
  @computed('comment.post.author.id', 'session.{personId,scopes.[]}')
  get canModerate() {
    // Ruleset:
    // - Allow LINC-ED Admins and Managers to moderate any community notice comment made by a Caregiver.
    // - Allow Tenant Admins to moderate any community notice comment made by a Caregiver.
    // - Allow Tenant Staff to moderate any community notice comment made by a Caregiver on the Staff's
    // post.
    // - Deny Caregivers, Learners from moderating any community notice comments.
    // - Deny all.

    // LINC-ED managers and admins, tenant admins
    if (
      this.permissions.meet(Scopes.LINCED_MANAGER) ||
      this.permissions.equal(Scopes.TENANT_ADMIN)
    ) {
      return true;
    }

    // Tenant staff who is the post author
    let postAuthorId = this.get('comment.post.author.id');
    let personId = this.get('session.personId');
    if (!isEmpty(postAuthorId)) {
      let isStaff = this.permissions.equal(Scopes.TENANT_STAFF);
      return isStaff && postAuthorId === personId;
    }

    // Anyone else is not allowed
    return false;
  }

  // reply
  @action
  handleReply(comment) {
    this.reply(comment);
    return false;
  }

  @action
  isProfane(comment) {
    const filter = new Filter({ exclude: this.safeWords });
    return filter.isProfane(comment);
  }

  @action
  toggleEditMode() {
    if (!this.isEditMode) {
      this.commentContent = this.comment.content;
      this.isEditMode = true;
      return;
    }

    this.isEditMode = false;
  }

  @action
  onSave() {
    this.isEditMode = false;
    this.edit(this.comment, this.commentContent);
  }

  @action
  handleDelete() {
    this.delete(this.comment);
    this.deleteDialogOpen = false;
  }

  @action
  approve() {
    this.heroApi.comments
      .approve(this.get('comment.id'))
      .then(() => {
        this.toaster.success(`Comment has been approved`);
      })
      .catch(() => {
        this.toaster.error(`Error approving the comment, please try again`);
      });
  }

  @action
  decline() {
    this.heroApi.comments
      .decline(this.get('comment.id'))
      .then(() => {
        this.toaster.success(`Comment has been declined`);
      })
      .catch(() => {
        this.toaster.error(`Error declining the comment, please try again`);
      });
  }

  // Lifecycle
  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);

    assert(
      'components::post-comment: comment is a required parameter',
      !isEmpty(this.comment),
    );
    assert(
      'components::post-comment: reply() is a required parameter',
      !isEmpty(this.reply),
    );

    // stage the comment ready for editing.
    if (!isEmpty(this.comment)) {
      this.commentContent = this.comment.content;
    }
  }
}
